import type { ICellRendererParams } from 'ag-grid-community'
import { Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { AccessKey, PublishableKey } from '@/api/admin-sales-channels-types'
import {
	useCreatePublishableKey,
	useCreateSecretKey,
	useDeletePublishableKey,
	useUpdatePublishableKey,
	useUpdateSecretKey
} from '@/api/api-mutations'
import { useListMarketingChannels, useListSalesChannels } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Copy } from '@/components/base/copy'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Prompt } from '@/components/base/prompt'
import { Switch } from '@/components/base/switch'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { useI18n } from '@/i18n/i18n-react'

// Reused by the POS/sales-channel access-key tables — only SECRET keys can drive a merchant-app
// kiosk session, so development mode is meaningless (and hidden) for PUBLISHABLE rows.
export function AccessKeyDevelopmentModeCell({ data }: ICellRendererParams<AccessKey>) {
	const updateSecretKey = useUpdateSecretKey(true)
	if (!data || data.type !== 'SECRET') return null
	return (
		<div className='flex h-full items-center' onClick={e => e.stopPropagation()}>
			<Switch
				size='small'
				checked={data.developmentMode}
				disabled={updateSecretKey.isPending}
				onCheckedChange={checked =>
					updateSecretKey.mutateAsync(
						{ path: { secretKeyUuid: data.uuid }, body: { developmentMode: checked } },
						{ onError: error => toast.error(error.message) }
					)
				}
			/>
		</div>
	)
}

interface CreateAccessKeyDrawerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	salesChannelUuids?: string[]
	pointOfSaleUuid?: string
	// Full editor mode used by the Access Keys settings page: renders sales-channel and
	// marketing-channel pickers so links can be managed key-side. When `editKey` is provided the
	// drawer edits that publishable key instead of creating a new one.
	manageLinks?: boolean
	editKey?: PublishableKey | null
}

export function CreateAccessKeyDrawer({
	open,
	onOpenChange,
	salesChannelUuids,
	pointOfSaleUuid,
	manageLinks = false,
	editKey = null
}: CreateAccessKeyDrawerProps) {
	const { L } = useI18n()
	const createPublishableKey = useCreatePublishableKey()
	const createSecretKey = useCreateSecretKey()
	const updatePublishableKey = useUpdatePublishableKey()
	const deletePublishableKey = useDeletePublishableKey()
	const [tokenDialogOpen, setTokenDialogOpen] = useState(false)
	const [createdToken, setCreatedToken] = useState('')

	const isEdit = editKey !== null
	// Both edit and "manage links" create flows are publishable-key only and expose the pickers.
	const linkMode = manageLinks || isEdit

	const salesChannelsQuery = useListSalesChannels()
	const marketingChannelsQuery = useListMarketingChannels()

	const salesChannelOptions = useMemo(
		() => (salesChannelsQuery.data ?? []).map(sc => ({ value: sc.uuid, label: sc.name })),
		[salesChannelsQuery.data]
	)
	const marketingChannelOptions = useMemo(
		() => (marketingChannelsQuery.data ?? []).map(mc => ({ value: mc.uuid, label: mc.name })),
		[marketingChannelsQuery.data]
	)

	const form = useAppForm({
		defaultValues: {
			name: editKey?.name ?? '',
			type: 'publishable' as 'publishable' | 'api',
			salesChannelUuids: editKey?.salesChannels?.map(sc => sc.uuid) ?? salesChannelUuids ?? [],
			marketingChannelUuid: editKey?.marketingChannel?.uuid ?? '',
			developmentMode: false
		},
		// Intentionally toast success + close unconditionally, not gated on `response.isSuccess` —
		// if the create call fails partway through a backend migration/retry, the operator can
		// just reopen the drawer and try again rather than getting stuck on a stale error state.
		onSubmit: async ({ value }) => {
			if (isEdit) {
				await updatePublishableKey({
					path: { publishableKeyUuid: editKey.uuid },
					body: {
						name: value.name,
						salesChannelUuids: value.salesChannelUuids,
						// Empty selection clears the link (null detaches; undefined would leave unchanged).
						marketingChannelUuid: value.marketingChannelUuid || null
					}
				})
				toast.success(L.Settings.AccessKeys.SaveSuccess())
				onOpenChange(false)
				return
			}

			if (linkMode) {
				await createPublishableKey({
					body: {
						name: value.name,
						salesChannelUuids: value.salesChannelUuids,
						marketingChannelUuid: value.marketingChannelUuid || undefined
					}
				})
				toast.success(L.General.Created())
				onOpenChange(false)
				return
			}

			if (value.type === 'api') {
				const response = await createSecretKey({
					body: {
						name: value.name,
						salesChannelUuids,
						pointOfSaleUuid,
						developmentMode: value.developmentMode
					}
				})
				if (response.isSuccess && response.response?.token) {
					setCreatedToken(response.response.token)
					setTokenDialogOpen(true)
				}
				toast.success(L.General.Created())
				onOpenChange(false)
			} else {
				await createPublishableKey({
					body: {
						name: value.name,
						salesChannelUuids,
						pointOfSaleUuid
					}
				})
				toast.success(L.General.Created())
				onOpenChange(false)
			}
		}
	})

	const handleDelete = async () => {
		if (!isEdit) return
		if (!window.confirm(L.Settings.AccessKeys.DeleteConfirm())) return
		await deletePublishableKey({ path: { publishableKeyUuid: editKey.uuid } })
		toast.success(L.Settings.AccessKeys.DeleteSuccess())
		onOpenChange(false)
	}

	const title = isEdit
		? L.Settings.AccessKeys.EditTitle()
		: manageLinks
			? L.Settings.AccessKeys.Create()
			: L.Settings.SalesChannels.Detail.CreateAccessKey()

	return (
		<>
			<Drawer open={open} onOpenChange={onOpenChange}>
				<Drawer.Content>
					<form.AppForm>
						<Drawer.Header>
							<Drawer.Title asChild>
								<Heading>{title}</Heading>
							</Drawer.Title>
						</Drawer.Header>
						<Drawer.Body className='flex-1 overflow-auto'>
							<FormContainer>
								<FormFieldGroup>
									<form.AppField
										name='name'
										validators={{
											onChange: ({ value }) => (!value ? L.General.Required() : undefined)
										}}
										children={field => (
											<field.TextField
												label={L.Fields.Name()}
												placeholder={L.Settings.SalesChannels.Detail.CreateAccessKey()}
												autoFocus
											/>
										)}
									/>
								</FormFieldGroup>
								{!linkMode && (
									<FormFieldGroup>
										<form.AppField
											name='type'
											children={field => (
												<field.SelectField
													label={L.Settings.SalesChannels.AccessKeyType.Title()}
													options={[
														{
															value: 'publishable',
															label: L.Settings.SalesChannels.AccessKeyType.Publishable()
														},
														{
															value: 'api',
															label: L.Settings.SalesChannels.AccessKeyType.Api()
														}
													]}
												/>
											)}
										/>
									</FormFieldGroup>
								)}
								{!linkMode && (
									<form.Subscribe selector={s => s.values.type}>
										{type =>
											type === 'api' && (
												<FormFieldGroup>
													<form.AppField
														name='developmentMode'
														children={field => (
															<field.BooleanField
																label={L.Settings.AccessKeys.Field.DevelopmentMode()}
																hint={L.Settings.AccessKeys.Field.DevelopmentModeHint()}
															/>
														)}
													/>
												</FormFieldGroup>
											)
										}
									</form.Subscribe>
								)}
								{linkMode && (
									<>
										<FormFieldGroup>
											<form.AppField
												name='salesChannelUuids'
												validators={{
													onChange: ({ value }) =>
														!value || value.length === 0
															? L.Settings.AccessKeys.Field.SalesChannelsRequired()
															: undefined
												}}
												children={field => (
													<field.ComboboxField
														label={L.Settings.AccessKeys.Field.SalesChannels()}
														placeholder={L.Settings.AccessKeys.Field.SalesChannelsPlaceholder()}
														options={salesChannelOptions}
														optionsLoading={salesChannelsQuery.isLoading}
													/>
												)}
											/>
										</FormFieldGroup>
										<FormFieldGroup>
											<form.AppField
												name='marketingChannelUuid'
												children={field => (
													<field.SingleComboboxField
														label={L.Settings.AccessKeys.Field.MarketingChannel()}
														placeholder={L.Settings.AccessKeys.Field.MarketingChannelPlaceholder()}
														options={marketingChannelOptions}
														optionsLoading={marketingChannelsQuery.isLoading}
														optional
													/>
												)}
											/>
										</FormFieldGroup>
									</>
								)}
							</FormContainer>
						</Drawer.Body>
						<Drawer.Footer>
							<div className='flex w-full items-center justify-end gap-x-2'>
								{isEdit && (
									<IconButton
										variant='transparent'
										tooltipText={L.General.Delete()}
										onClick={handleDelete}
										className='mr-auto'
									>
										<Trash2 className='size-4' />
									</IconButton>
								)}
								<Button variant='secondary' size='small' onClick={() => onOpenChange(false)}>
									{L.General.Cancel()}
								</Button>
								{isEdit ? <form.SaveButton /> : <form.CreateButton />}
							</div>
						</Drawer.Footer>
					</form.AppForm>
				</Drawer.Content>
			</Drawer>

			<Prompt open={tokenDialogOpen} variant='confirmation'>
				<Prompt.Content>
					<Prompt.Header>
						<Prompt.Title>{L.Settings.SalesChannels.TokenCreated.Title()}</Prompt.Title>
						<Prompt.Description>{L.Settings.SalesChannels.TokenCreated.Description()}</Prompt.Description>
					</Prompt.Header>
					<div className='mx-6 mt-4 rounded-md border border-ui-border-base bg-ui-bg-subtle px-4 py-3'>
						<div className='flex items-center justify-between gap-2'>
							<code className='flex-1 break-all text-xs'>{createdToken}</code>
							<Copy content={createdToken} variant='mini' />
						</div>
					</div>
					<Prompt.Footer>
						<Prompt.Action onClick={() => setTokenDialogOpen(false)}>{L.General.Close()}</Prompt.Action>
					</Prompt.Footer>
				</Prompt.Content>
			</Prompt>
		</>
	)
}
